import React, { useRef } from 'react';
import styles from './Wall.scss';
import classNames from 'classnames';
import { NavImage } from '../../mobile-menu-item/NavImage/NavImage';
import { NavigationItemType } from '../../mobile-menu-item/itemTypes';
import { Category } from '../../constants';
import { useTopLevelTabLoopFocus } from '../../hooks/useTopLevelTabLoopFocus';

type TabLoopCloseButton = HTMLElement & {
  __tabLoopHandler?: (event: KeyboardEvent) => void;
};

export const Brick = ({
  position,
  isLarge,
  url,
  imageUrl,
  label,
  isTabbable = true,
}: {
  position: string;
  link: MenuItem;
  isLarge?: boolean;
  url: string;
  imageUrl: string;
  label: string;
  isTabbable?: boolean;
}) => {
  return (
    <div className={classNames(styles.brick, styles[`brick-${position}`])}>
      <NavImage
        asAnchor
        imageUrl={imageUrl}
        url={url}
        itemType={
          isLarge
            ? NavigationItemType.WALL_LARGE
            : NavigationItemType.WALL_SMALL
        }
        label={label}
        tabIndex={isTabbable ? undefined : -1}
      />
    </div>
  );
};

export const Wall = ({
  tab,
  links,
  isTabbable = true,
  focusContainerRef,
}: {
  tab: Category;
  links: MenuItem[];
  isTabbable?: boolean;
  focusContainerRef?: React.RefObject<HTMLElement | null>;
}) => {
  const wallRef = useRef<HTMLDivElement | null>(null);

  const { focusActiveTab } = useTopLevelTabLoopFocus({
    containerRef: focusContainerRef ?? wallRef,
    isEnabled: isTabbable,
    activeTabSelector: 'button[data-current-tab="true"][tabindex="0"]',
  });

  const handleWallTabLoopSentinelFocus = () => {
    const closeButton = document.getElementById(
      'mobile-menu-toggle'
    ) as TabLoopCloseButton | null;

    if (!closeButton) {
      focusActiveTab();
      return;
    }

    closeButton.__tabLoopHandler &&
      closeButton.removeEventListener('keydown', closeButton.__tabLoopHandler);

    closeButton.__tabLoopHandler = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || event.shiftKey) {
        return;
      }

      event.preventDefault();
      closeButton.__tabLoopHandler &&
        closeButton.removeEventListener(
          'keydown',
          closeButton.__tabLoopHandler
        );
      delete closeButton.__tabLoopHandler;
      focusActiveTab();
    };

    closeButton.addEventListener('keydown', closeButton.__tabLoopHandler);
    closeButton.focus();
  };

  if (!links || links.length === 0) {
    return null;
  }
  // rearrange so the first and second link appear in first and sixth position (large images) on mobile
  const item = links.splice(1, 1)[0];
  links.splice(5, 0, item);

  return (
    <div
      ref={wallRef}
      data-testid="wall-panel"
      className={classNames(
        styles.wall,
        styles[`wall-${tab.toLowerCase().split(' ').join('-')}`]
      )}
    >
      {links.map((link, index) => {
        const { name, url, imageUrl } = link;
        if (!name || !url) return null;
        const isLarge = index === 0 || index === 5;
        return (
          <Brick
            key={`${tab}-${name}-${index + 1}`}
            link={link}
            position={`p${index + 1}`}
            isLarge={isLarge}
            url={url || ''}
            imageUrl={imageUrl || ''}
            label={name}
            isTabbable={isTabbable}
          />
        );
      })}
      {isTabbable && links.length > 0 && (
        <button
          type="button"
          className={styles.tabLoopSentinel}
          data-testid="wall-tab-loop-sentinel"
          onFocus={handleWallTabLoopSentinelFocus}
          aria-label="Return to current tab"
        />
      )}
    </div>
  );
};
